import type {
  QuestionnaireChatState,
  QuestionnaireChatTurnFailureDetail,
  QuestionnaireChatTurnReadback,
  QuestionnaireChatTurnStageEvent,
  QuestionnaireChatTurnStatusEvent,
  QuestionnaireTurnCommand,
  QuestionnaireTurnInput,
} from "@digital-twins/shared-types";
import {
  isQuestionnaireChatTurnTerminal,
  openClarificationOf,
} from "@digital-twins/shared-types";

import {
  enqueueQuestionnaireChatTurnApi,
  getQuestionnaireChatApi,
  getQuestionnaireChatTurnApi,
  getQuestionnaireDraftApi,
} from "@/features/studies/questionnaire/api/questionnaireDraftsApi";
import type {
  QuestionnaireChatApiResponse,
  QuestionnaireTurnEndCause,
} from "@/features/studies/questionnaire/types";
import {
  classifyTransportFailure,
  noAnswerArrived,
} from "@/lib/api/transportFailure";
import {
  onQuestionnaireChatTurnStage,
  onQuestionnaireChatTurnStatus,
  subscribeToQuestionnaireDraft,
} from "@/lib/services/websocket";
import { createRealtimeRepairScheduler } from "@/lib/realtime/realtimeRepairScheduler";
import type { RealtimeRepairScheduler } from "@/lib/realtime/realtimeRepairScheduler";
import { readUntilAnswered, turnCancelledError } from "./readUntilAnswered";

/**
 * Quiet period tolerated while a turn is in flight before assuming an event was
 * lost. Replaces the old unconditional 5s poll: the turn is driven by
 * `:turn-stage` / `:turn-status` events, and this only fires when those stop
 * arriving — a dead worker, or a gap the reconnect path did not catch.
 *
 * Sized well above the gap between stage events so a merely slow turn is never
 * mistaken for a stalled one.
 */
export const QUESTIONNAIRE_CHAT_TURN_STALL_MS = 30_000;

/**
 * A durable turn that ended `failed`. It carries the turn's typed code in the
 * same `response.data` shape an HTTP failure has, so `formatTurnFailureCopy`
 * maps it to the reviewed copy instead of showing the raw backend sentence.
 * `retryable` is the failing owner's own verdict, persisted on the turn: the
 * live event and a readback both carry it. When the owner gave none, no retry
 * is offered.
 */
export class QuestionnaireChatTurnFailedError extends Error {
  readonly response: {
    data: {
      code: string | null;
      message: string;
      failureCodes: Array<string>;
      failureDetails: Array<QuestionnaireChatTurnFailureDetail>;
      retryable: boolean;
    };
  };

  constructor(turn: QuestionnaireTurnEndCause) {
    const message = turn.errorMessage ?? "Questionnaire chat turn failed";
    super(message);
    this.name = "QuestionnaireChatTurnFailedError";
    this.response = {
      data: {
        code: turn.errorCode ?? null,
        message,
        failureCodes: turn.failureCodes ?? [],
        failureDetails: turn.failureDetails ?? [],
        retryable: turn.retryable === true,
      },
    };
  }
}

/**
 * Whether its readback says this waiter's turn is dead: still in flight on
 * the row, but silent past the server's liveness window (`stale`), with no
 * commit recorded, so nothing was saved. It ends as the reaper would end it:
 * failed with the retryable `turn_stale`. A dead turn whose commit landed is
 * the reaper's to end `completed`, and a later read settles it.
 */
function turnDied(readback: QuestionnaireChatTurnReadback): boolean {
  return readback.stale === true && readback.turn?.draftRevisionAfter == null;
}

const DEAD_TURN = {
  errorCode: "turn_stale",
  retryable: true,
} as const;

/**
 * Watch one turn until it ends (plan S4.4). Its socket events drive it, and
 * its own row, read by id, is the authority: read at once, again on every
 * repair (a stall, a reconnect) and on its terminal event. A failed read is
 * not an end: it is sent again until it answers (`readUntilAnswered`). The
 * wait ends only on this turn's typed end (completed, failed, cancelled), on
 * `stale` (dead, nothing saved), on a read no retry changes (an expired
 * session, no access, the turn or draft gone), or on `abortSignal`.
 */
export async function waitForQuestionnaireChatTurn(args: {
  draftId: string;
  /**
   * The turn this waiter settles on. Status events are draft-scoped, and a
   * subscribe replays the draft's latest turn, which can still be the previous
   * one; only this turn's terminal status ends the wait.
   */
  turnId: string;
  onStage?: (event: QuestionnaireChatTurnStageEvent) => void;
  abortSignal?: AbortSignal;
}): Promise<QuestionnaireChatApiResponse> {
  const { draftId, turnId, abortSignal, onStage } = args;
  if (abortSignal?.aborted) throw turnCancelledError();

  subscribeToQuestionnaireDraft(draftId);
  return new Promise<QuestionnaireChatApiResponse>((resolve, reject) => {
    let ended = false;
    let settling = false;
    let reading = false;
    let readAgain = false;
    let readOnce = false;
    let sawStage = false;
    let repair: RealtimeRepairScheduler | undefined;
    let offStatus = () => {};
    let offStage = () => {};
    const stopListening = () => {
      offStatus();
      offStage();
      offStatus = () => {};
      offStage = () => {};
      repair?.stop();
      repair = undefined;
    };
    // One end. It is handed over a microtask later, so a cancel that
    // arrives with it takes precedence: the page no longer shows the turn.
    const end = (outcome: () => void) => {
      if (ended) return;
      ended = true;
      stopListening();
      abortSignal?.removeEventListener("abort", onAbort);
      queueMicrotask(() =>
        abortSignal?.aborted ? reject(turnCancelledError()) : outcome(),
      );
    };
    const onAbort = () => end(() => reject(turnCancelledError()));
    // The terminal facts, as a status event or the turn's row carries them.
    const endOn = (
      turn: Pick<
        QuestionnaireChatTurnStatusEvent,
        | "status"
        | "errorCode"
        | "errorMessage"
        | "failureCodes"
        | "failureDetails"
        | "retryable"
      >,
    ) => {
      if (turn.status === "failed") {
        end(() => reject(new QuestionnaireChatTurnFailedError(turn)));
      } else {
        end(() => reject(turnCancelledError()));
      }
    };

    const read = async (): Promise<void> => {
      if (ended || settling) return;
      if (reading) {
        readAgain = true;
        return;
      }
      reading = true;
      try {
        const readback = (await readUntilAnswered(
          () => getQuestionnaireChatTurnApi(draftId, turnId),
          abortSignal,
        )) as QuestionnaireChatTurnReadback;
        if (ended || settling) return;
        const turn = readback.turn;
        const first = !readOnce;
        readOnce = true;
        if (!turn) return;
        if (!isQuestionnaireChatTurnTerminal(turn.status)) {
          if (turnDied(readback)) {
            end(() => reject(new QuestionnaireChatTurnFailedError(DEAD_TURN)));
            return;
          }
          // The listeners attach once the enqueue answers, so a stage the
          // turn reached before (PLAN starts at once) sent no event this
          // waiter heard. The first read carries it, unless a live event for
          // this turn has already said where it is.
          if (first && !sawStage && turn.stage) {
            onStage?.({ draftId, turnId, stage: turn.stage });
          }
          return;
        }
        if (turn.status !== "completed") {
          endOn(turn);
          return;
        }
        settling = true;
        stopListening();
        const settled = await settleFromReadback(draftId, readback, abortSignal);
        end(() => resolve(settled));
      } catch (error) {
        // A read no retry changes (401, 403, 404). An abort has ended the
        // wait already.
        end(() => reject(error));
      } finally {
        reading = false;
        if (readAgain) {
          readAgain = false;
          void read();
        }
      }
    };

    offStatus = onQuestionnaireChatTurnStatus(draftId, (payload) => {
      if (payload.turnId !== turnId) return;
      repair?.notifyActivity();
      if (!isQuestionnaireChatTurnTerminal(payload.status)) return;
      // A finished turn is settled from its row and the draft it left; a
      // failed or cancelled one ends on the event itself.
      if (payload.status === "completed") void read();
      else endOn(payload);
    });
    // Stage events are draft-scoped too: only this turn's move its stage.
    offStage = onQuestionnaireChatTurnStage(draftId, (payload) => {
      if (payload.turnId !== turnId) return;
      sawStage = true;
      repair?.notifyActivity();
      onStage?.(payload);
    });
    abortSignal?.addEventListener("abort", onAbort);
    // Repair, not poll: the events drive the turn, and this reads its row
    // when they stop arriving (a dead worker) or the socket came back after
    // a gap. Re-subscribing makes the gateway replay the terminal status.
    repair = createRealtimeRepairScheduler({
      stallTimeoutMs: QUESTIONNAIRE_CHAT_TURN_STALL_MS,
      onRepair: () => {
        subscribeToQuestionnaireDraft(draftId);
        void read();
      },
    });
    void read();
  });
}

export async function enqueueAndAwaitQuestionnaireChatTurn(args: {
  draftId: string;
  /**
   * The user's send, minted once per send and kept by its Retry: the server
   * answers a re-sent id with the turn it already queued.
   */
  clientTurnId: string;
  /** The draft revision the user sees; the turn acts on it or is refused (S2c). */
  expectedRevision: number;
  /** The user's words; absent when the turn carries a command. */
  message?: string;
  /** A UI fact the turn carries instead of a message (plan S4.2). */
  command?: QuestionnaireTurnCommand;
  language?: string;
  /** The card option the message chose (plan S3.3, L16). */
  optionId?: string;
  /** The open clarification record the turn answers (plan S4.1). */
  answers?: string;
  onStage?: (event: QuestionnaireChatTurnStageEvent) => void;
  onTurnId?: (turnId: string) => void;
  /**
   * The turn's input as its row stores it, from the enqueue's answer: the
   * bubble is drawn from it from the start (an objective edit's change).
   */
  onInput?: (input: QuestionnaireTurnInput) => void;
  /**
   * Whether the send is being sent again because no answer arrived (true),
   * until an answer does (false).
   */
  onSendUnconfirmed?: (unconfirmed: boolean) => void;
  abortSignal?: AbortSignal;
}): Promise<QuestionnaireChatApiResponse> {
  subscribeToQuestionnaireDraft(args.draftId);
  const body = {
    ...(args.command ? { command: args.command } : { message: args.message }),
    language: args.language,
    clientTurnId: args.clientTurnId,
    expectedRevision: args.expectedRevision,
    ...(args.optionId ? { optionId: args.optionId } : {}),
    ...(args.answers ? { answers: args.answers } : {}),
  };
  // The send ends on its first HTTP answer, whatever the status: a typed
  // refusal says its own line, an uncoded 5xx its transport line. Only a send
  // no answer reached (offline, a dropped connection, a timeout) is sent
  // again, with the same `clientTurnId`, which the server answers with the
  // turn it may already have queued.
  let unconfirmed = false;
  const enqueued = await readUntilAnswered(
    async () => {
      try {
        return await enqueueQuestionnaireChatTurnApi(
          args.draftId,
          body,
          args.abortSignal,
        );
      } catch (error) {
        if (!unconfirmed && noAnswerArrived(classifyTransportFailure(error))) {
          unconfirmed = true;
          args.onSendUnconfirmed?.(true);
        }
        throw error;
      }
    },
    args.abortSignal,
    (error) => !noAnswerArrived(classifyTransportFailure(error)),
  );
  if (unconfirmed) args.onSendUnconfirmed?.(false);
  if (enqueued.turnId) {
    args.onTurnId?.(enqueued.turnId);
  }
  if (enqueued.input) {
    args.onInput?.(enqueued.input);
  }
  return waitForQuestionnaireChatTurn({ ...args, turnId: enqueued.turnId });
}

/** A completed turn, settled from its readback and the draft it left. */
async function settleFromReadback(
  draftId: string,
  readback: QuestionnaireChatTurnReadback,
  abortSignal?: AbortSignal,
): Promise<QuestionnaireChatApiResponse> {
  const completedChatState =
    readback.chatState ??
    (await readUntilAnswered(() => getQuestionnaireChatApi(draftId), abortSignal));
  const draftResponse = await readUntilAnswered(
    () => getQuestionnaireDraftApi(draftId),
    abortSignal,
  );
  // The stored transcript is raw artifact JSON; one that carries messages is
  // the chat state this revision wrote.
  const storedChat = draftResponse.artifacts.chatTranscript;
  const chatState = Array.isArray(storedChat?.messages)
    ? (storedChat as unknown as QuestionnaireChatState)
    : completedChatState;
  // The turn's reply is the transcript's last message when the assistant
  // wrote it; a command recorded with no reply (plan S4.2) has none, so an
  // earlier reply's effects are never announced again.
  const lastMessage = chatState.messages?.at(-1);
  const lastAssistant =
    lastMessage?.role === "assistant" ? lastMessage : undefined;
  return {
    // The turn asked when it left a question open (plan S4.1, SEAM-B-25).
    status: openClarificationOf(chatState) ? "ask_follow_up" : "draft_ready",
    assistantMessage: lastAssistant
      ? {
          id: lastAssistant.id,
          role: "assistant",
          content: lastAssistant.content,
          kind: lastAssistant.kind,
          createdAt: lastAssistant.createdAt,
          options: lastAssistant.options,
          collect: lastAssistant.collect,
          // A chat revision's effects ride on its message (plan S4.3).
          ...(lastAssistant.effects?.length
            ? { effects: lastAssistant.effects }
            : {}),
        }
      : {
          id: "turn-complete",
          role: "assistant",
          content: "",
          kind: "system_status",
          createdAt: new Date().toISOString(),
        },
    assumptions: chatState.assumptions ?? [],
    chatState,
    draft: draftResponse.artifacts?.draft,
    readiness: draftResponse.readiness,
    // The GET owns this draft's bytes; a completed turn may describe an older revision.
    revision: draftResponse.revision,
    // A completed turn carries a cause only when it stopped after a commit.
    ...(readback.turn?.status === "completed" && readback.turn.errorCode
      ? {
          stoppedOn: {
            errorCode: readback.turn.errorCode,
            errorMessage: readback.turn.errorMessage,
            failureCodes: readback.turn.failureCodes,
            failureDetails: readback.turn.failureDetails,
            retryable: readback.turn.retryable,
          },
        }
      : {}),
  };
}
